// src/features/rooms/spaceValidationSelectors.ts
import { createSelector } from "@reduxjs/toolkit";
import { IRootState } from "../../../app/store";
import { selectGetMcBimSpacesParams } from "../../../McBim/McBimSlice";
import {
  ISpaceValidation,
  SPACE_VALIDATION_ID_ENUM,
  SPACE_VALIDATIONS,
  getValidationsForSpace,
} from "../../../RoomSideBar/SpaceValidations/spaceValidationsUtils";
import { PartialMcBimSpace } from "../../../utils";
import { selectCompCodePath } from "../../../globalSelectors";
import {
  selectHttpDevices,
  selectRoomsWhereDevicesIsNotPresentInSelectedRooms,
} from "../deviceSelectors";
import { IDevice } from "@multiconsult-group/multiplacer-core";
import { selectHttpGetRoomsCurrentFloorResult } from "../../../Room/httpRoomSelectors";
import { selectHttpMcBimSpaceItems } from "../../../McBim/httpMcBimSelectors";

type ValidationStatus = ISpaceValidation["status"]; // "VALID" | "WARNING" | ...
type ValidationId = SPACE_VALIDATION_ID_ENUM;

const selectedRooms = (s: IRootState) => s.design.selectedRooms;

export type ISelectedRoomsValidationColumn = {
  id: ValidationId;
  title: string;
  shortTitle?: string;
};
export type ISelectedRoomValidationRow = {
  roomNumber: string;
  roomName?: string; // ✅ ny
  hasDevices: boolean;
  allValid: boolean;
  validations: ISpaceValidation[];
  statusByValidationId: Record<string, ValidationStatus | "NOT_APPLICABLE">;
};

export type ISelectedRoomsValidationMatrix = {
  selectedRoomsCount: number;

  roomsWithDevicesCount: number;
  roomsWithoutDevicesCount: number;

  approvedRoomsCount: number;
  notApprovedRoomsCount: number;

  columns: ISelectedRoomsValidationColumn[];
  rows: ISelectedRoomValidationRow[];
};

const selectActiveSpaceValidationSpecs = (state: IRootState) =>
  state.design.activeSpaceValidationSpecs;
// src/features/rooms/spaceValidationSelectors.ts
// src/features/rooms/spaceValidationSelectors.ts
// src/features/rooms/spaceValidationSelectors.ts
export const selectSpaceValidationsMatrixForSelectedRoomsExcludingStandardRoom = createSelector(
  [
    selectedRooms,
    selectHttpDevices,
    selectRoomsWhereDevicesIsNotPresentInSelectedRooms,
    selectHttpGetRoomsCurrentFloorResult,
    selectActiveSpaceValidationSpecs,
    selectCompCodePath,
    selectGetMcBimSpacesParams,
    selectHttpMcBimSpaceItems,
  ],
  (
    selectedRoomNumbers,
    devices,
    roomsWithoutDevices,
    roomsMap,
    activeSpecs,
    compCodePath,
    _spacesArgs,
    spacesData
  ) => {
    const selectedRoomsCount = selectedRoomNumbers?.length ?? 0;
    const roomsWithoutDevicesCount = roomsWithoutDevices.length;

    const specsWithoutStandardRoom = {
      ...activeSpecs,
      [SPACE_VALIDATION_ID_ENUM.STANDARD_ROOM]: false,
    };

    const columns = SPACE_VALIDATIONS.filter(
      (v) => specsWithoutStandardRoom[v.id] && v.id !== SPACE_VALIDATION_ID_ENUM.STANDARD_ROOM
    ).map((v) => ({
      id: v.id,
      title: v.title,
      shortTitle: (v as any).shortTitle,
    }));

    if (!selectedRoomNumbers || selectedRoomNumbers.length === 0) {
      return {
        selectedRoomsCount: 0,
        roomsWithDevicesCount: 0,
        roomsWithoutDevicesCount: 0,
        approvedRoomsCount: 0,
        notApprovedRoomsCount: 0,
        columns,
        rows: [],
      };
    }

    if (!compCodePath || !spacesData) {
      return {
        selectedRoomsCount,
        roomsWithDevicesCount: 0,
        roomsWithoutDevicesCount,
        approvedRoomsCount: 0,
        notApprovedRoomsCount: 0,
        columns,
        rows: selectedRoomNumbers.map((roomNumber) => {
          const room = roomsMap[roomNumber] ?? null;
          const emptyStatusMap = Object.fromEntries(
            columns.map((c) => [c.id, "NOT_APPLICABLE"])
          ) as Record<string, ValidationStatus | "NOT_APPLICABLE">;

          return {
            roomNumber,
            roomName: room?.roomName ?? undefined, // ✅
            hasDevices: false,
            allValid: false,
            validations: [],
            statusByValidationId: emptyStatusMap,
          };
        }),
      };
    }

    const spaceByName = new Map<string, PartialMcBimSpace>();
    for (const s of spacesData) spaceByName.set(s.name, s as PartialMcBimSpace);

    const rows = selectedRoomNumbers.map((roomNumber) => {
      const room = roomsMap[roomNumber] ?? null;
      const space = spaceByName.get(roomNumber) ?? null;

      const devicesInRoom = devices.filter((d: IDevice) => d.roomNumber === roomNumber);
      const hasDevices = devicesInRoom.length > 0;

      const emptyStatusMap = Object.fromEntries(
        columns.map((c) => [c.id, "NOT_APPLICABLE"])
      ) as Record<string, ValidationStatus | "NOT_APPLICABLE">;

      if (!hasDevices || !room || !space) {
        return {
          roomNumber,
          roomName: room?.roomName ?? undefined, // ✅
          hasDevices,
          allValid: false,
          validations: [],
          statusByValidationId: emptyStatusMap,
        };
      }

      const validations = getValidationsForSpace({
        singleRoom: room,
        singleSpace: space,
        devicesInRoom,
        mcbimItemsInStandardSpace: [],
        activeSpaceValidationSpecs: specsWithoutStandardRoom,
        compCodePath,
      });

      const statusByValidationId = { ...emptyStatusMap };
      validations.forEach((v) => {
        if (statusByValidationId[v.id] !== undefined) {
          statusByValidationId[v.id] = v.status;
        }
      });

      const allValid =
        columns.length > 0 && columns.every((c) => statusByValidationId[c.id] === "VALID");

      return {
        roomNumber,
        roomName: room.roomName ?? undefined, // ✅
        hasDevices,
        allValid,
        validations,
        statusByValidationId,
      };
    });

    const roomsWithDevicesCount = rows.filter((r) => r.hasDevices).length;
    const approvedRoomsCount = rows.filter((r) => r.hasDevices && r.allValid).length;
    const notApprovedRoomsCount = rows.filter((r) => r.hasDevices && !r.allValid).length;

    return {
      selectedRoomsCount,
      roomsWithDevicesCount,
      roomsWithoutDevicesCount,
      approvedRoomsCount,
      notApprovedRoomsCount,
      columns,
      rows,
    };
  }
);
